// UI strings for the speaking ladder's steps 4–5 (docs/PEDAGOGY.md §3.2, 2026-10-09): the guided
// tasks with their role cards (Ohjattu keskustelu), the 4/3/2 talk and the scenes in character
// (Vapaasti ja hahmossa), the lessons' doors and the week's Yhdessä line
// (together/speaking/Guided.tsx, FreeTalk.tsx, SpeakingStep.tsx, WeekSpeaking.tsx).
import { defineArea } from '../area';

const fiTasks = (n: number) => (n === 1 ? 'yksi tehtävä' : `${n} tehtävää`);

export const speakingTasksFi = {
  // step 4
  newTask: 'Uusi tehtävä',
  repeatTask: 'Viime kerran tehtävä, roolit vaihdettuina',
  yourCard: 'Sinun korttisi',
  partnerCard: (name: string) => `${name} näkee eri kortin.`,
  keepHidden: 'Älä näytä korttiasi. Kysy ja kerro valyriaksi.',
  youKnow: 'Tiedät',
  findOut: 'Selvitä',
  words: 'Sanoja',
  help: 'Mallilauseet',
  helpUsed: (n: number) => (n === 1 ? 'Avasit yhden mallilauseen.' : `Avasit ${n} mallilausetta.`),
  showModel: (i: number) => `Näytä mallilause ${i}`,
  foundOutAsk: 'Saitteko selville?',
  foundOut: 'Saimme selville',
  notFoundOut: 'Ei tällä kertaa',
  persona: 'Hahmossa',
  // step 5
  topic: 'Aihe',
  rule: 'Sama aihe kolme kertaa: 4, 3 ja 2 minuuttia. Vain valyriaa. Yhdellä sovitulla suomen sanalla saa pyytää apua.',
  round: (i: number, min: number) => `Kierros ${i}/3 · ${min} min`,
  leads: (name: string) => `${name} aloittaa`,
  youLead: 'Sinä aloitat',
  startTimer: 'Käynnistä ajastin',
  pause: 'Tauko',
  resume: 'Jatka',
  nextRound: 'Seuraava kierros',
  timeUp: 'Aika loppui.',
  yourPersona: 'Sinun hahmosi',
  partnerPersona: (name: string, who: string) => `${name}: ${who}`,
  personaLines: 'Repliikkejä sarjasta',
  howWasIt: 'Toimiko keskustelu?',
  worked: 'Toimi',
  didNotWork: 'Ei toiminut',
  record: 'Äänitä keskustelu, jos haluat',
  // the round's end
  scoreGuided: (name: string, ok: number, n: number) => `${name} ymmärsi ${ok}/${n}`,
  scoreTask: (name: string, done: boolean, help: number) =>
    `${name}: ${done ? 'sai selville' : 'ei saanut selville'}, ${help === 1 ? 'yksi mallilause' : `${help} mallilausetta`}`,
  scoreFree: (name: string, ok: boolean) => `${name}: ${ok ? 'toimi' : 'ei toiminut'}`,
  // the ladder (step 4's bar counts tasks)
  needsTasks: (n: number) =>
    n === 1
      ? 'Seuraavaan askeleeseen: vielä yksi tehtävä, jossa kumpikin avaa enintään kaksi mallilausetta.'
      : `Seuraavaan askeleeseen: vielä ${n} tehtävää peräkkäin, joissa kumpikin avaa enintään kaksi mallilausetta.`,
  personNeedsTasks: (name: string, n: number) => `${name}: vielä ${fiTasks(n)}`,
  /** A line of ours built from one of Peterson's sentences. */
  built: 'harjoituslause, koottu Petersonin lauseesta',
  // the lessons that open the steps (<SpeakingStep />)
  lesson: {
    here: (n: number, step: string) => `Olette nyt askeleella ${n}/5: ${step}.`,
    personas: (n: number) => (n === 1 ? 'Yksi kohtaus hahmossa on avoinna.' : `${n} kohtausta hahmossa on avoinna.`),
    open: 'Avaa Puhutaan-osio',
  },
  // the week's Yhdessä line
  week: {
    title: 'Puhutaan',
    task: (title: string) => `tehtävä: ${title}`,
    topic: (title: string) => `aihe: ${title}`,
    opens: (step: string) => `tämän oppitunnin jälkeen avautuu ${step}`,
  },
};

export type SpeakingTasksMessages = typeof speakingTasksFi;

const enTasks = (n: number) => (n === 1 ? 'one more task' : `${n} more tasks`);

export const speakingTasksEn: SpeakingTasksMessages = {
  newTask: 'New task',
  repeatTask: 'Last time’s task, roles swapped',
  yourCard: 'Your card',
  partnerCard: (name) => `${name} sees a different card.`,
  keepHidden: 'Do not show your card. Ask and tell in High Valyrian.',
  youKnow: 'You know',
  findOut: 'Find out',
  words: 'Words',
  help: 'Model phrases',
  helpUsed: (n) => (n === 1 ? 'You opened one model phrase.' : `You opened ${n} model phrases.`),
  showModel: (i) => `Show model phrase ${i}`,
  foundOutAsk: 'Did you find out?',
  foundOut: 'We found out',
  notFoundOut: 'Not this time',
  persona: 'In character',
  topic: 'Topic',
  rule: 'The same topic three times: 4, 3 and 2 minutes. High Valyrian only; one agreed Finnish word may help.',
  round: (i, min) => `Round ${i}/3 · ${min} min`,
  leads: (name) => `${name} starts`,
  youLead: 'You start',
  startTimer: 'Start the timer',
  pause: 'Pause',
  resume: 'Resume',
  nextRound: 'Next round',
  timeUp: 'Time is up.',
  yourPersona: 'Your character',
  partnerPersona: (name, who) => `${name}: ${who}`,
  personaLines: 'Lines from the show',
  howWasIt: 'Did the talk work?',
  worked: 'It worked',
  didNotWork: 'It did not work',
  record: 'Record the talk if you like',
  scoreGuided: (name, ok, n) => `${name} understood ${ok}/${n}`,
  scoreTask: (name, done, help) => `${name}: ${done ? 'found out' : 'not found out'}, ${help === 1 ? 'one model phrase' : `${help} model phrases`}`,
  scoreFree: (name, ok) => `${name}: ${ok ? 'it worked' : 'it did not work'}`,
  needsTasks: (n) => `Next step: ${enTasks(n)} in a row where each of you opens at most two model phrases.`,
  personNeedsTasks: (name, n) => `${name}: ${enTasks(n)}`,
  built: 'practice sentence, built from one of Peterson’s',
  lesson: {
    here: (n, step) => `You are on step ${n}/5: ${step}.`,
    personas: (n) => (n === 1 ? 'One scene in character is open.' : `${n} scenes in character are open.`),
    open: 'Open Let’s talk',
  },
  week: {
    title: 'Let’s talk',
    task: (title) => `task: ${title}`,
    topic: (title) => `topic: ${title}`,
    opens: (step) => `after this lesson ${step} opens`,
  },
};

// Messages.speakingTasks (i18n/area.ts: collected by area-registry.ts)
export const area = defineArea('speakingTasks', speakingTasksFi, speakingTasksEn);
declare module '../area' {
  interface AreaMessages extends Record<typeof area.key, typeof speakingTasksFi> {}
}
